import { ChallengeService } from '@duck/api-definitions'
import { WysiwygEditor } from '@components/WysiwygEditor'
import { useSetAtom, useAtomValue } from 'jotai'
import { useTar } from '@/modules/i18n'
import { MarkdownEditor, ResizablePanelGroup } from '@duck/ui'
import { useState, useRef, useCallback } from 'react'
import { useEventEmitter, useEventListener } from '@/modules/EventBusModule'
import { WorkspacePane } from '../Workspace/WorkspacePane'
import { currentSolutionAtom } from '@/store/saveWorkAtoms'
import { persistPanesLayout } from '../DataWorkspace/utils'
import { WorkspaceResizeHandle } from '../Workspace/WorkspaceResizeHandle'
import { debounce } from 'lodash'
import { workspaceLayoutAtom } from '@/store/workspaceAtoms'

interface DocumentsWorkspaceProps {
    task: ChallengeService.ChallengeInfo['task']
    charts: ChallengeService.WorkState['charts']
    initialSolution?: string
    handleSolutionChange: (solution: string) => void
    handleChartAdded: (solution: string, chart: ChallengeService.Chart) => void
    handleChartRemoved: (solution: string, remainingChartIds: string[]) => void
}

export const DocumentsWorkspace = ({
    task,
    charts,
    initialSolution = '',
    handleSolutionChange,
    handleChartAdded,
    handleChartRemoved,
}: DocumentsWorkspaceProps) => {
    const { t } = useTar()
    const setCurrentSolution = useSetAtom(currentSolutionAtom)
    const workspaceLayout = useAtomValue(workspaceLayoutAtom)
    const emitChartsTriggerRedraw = useEventEmitter('charts:triggerRedraw', 10)
    const emitWorkspaceRepaintSolution = useEventEmitter('workspace:repaintSolution', 10)
    const [rerenderKey, setRerenderKey] = useState(0)
    const localSolutionRef = useRef<string>(initialSolution)

    useEventListener('workspace:repaintSolution', () => {
        setCurrentSolution(currentSolution => {
            localSolutionRef.current = currentSolution
            setRerenderKey(prev => prev + 1)
            setTimeout(() => emitChartsTriggerRedraw({}), 64)
            return currentSolution
        })
    }, [setCurrentSolution, setRerenderKey, emitChartsTriggerRedraw])

    const onLayoutChange = useCallback(debounce((sizes: number[]) => {
        if (sizes.length !== 2) {
            return
        }
        persistPanesLayout.save('documents', {
            assignment: sizes[0],
            solution: sizes[1]
        })
    }, 20), [])

    return (
        <ResizablePanelGroup key={rerenderKey} direction={workspaceLayout === 'split-side' ? 'vertical' : 'horizontal'} className="h-full" onLayout={onLayoutChange}>
            <WorkspacePane
                paneKey="assignment"
                layoutName="documents"
                title={t('common.assignment')}
                minSize={30}
                maxSize={70}
                showContentZoom
                onToggle={() => {
                    emitWorkspaceRepaintSolution({})
                }}
            >
                <MarkdownEditor
                    defaultValue={task.body}
                    className="max-w-none flex-1"
                    mode="view"
                    mdClassName="overflow-visible border-none"
                />
            </WorkspacePane>

            <WorkspaceResizeHandle enablingPanes={['assignment', 'solution']} repaintOnPointerUp={workspaceLayout === 'split-top'} />

            <WorkspacePane
                paneKey="solution"
                layoutName="documents"
                beforeFullscreen={() => {
                    return new Promise<void>(resolve => {
                        setCurrentSolution(currentSolution => {
                            localSolutionRef.current = currentSolution
                            setTimeout(() => {
                                resolve()
                            }, 10)
                            return currentSolution
                        })
                    })
                }}
                onFullscreen={(open: boolean) => {
                    if (!open) {
                        emitWorkspaceRepaintSolution({})
                    }
                }}
                onToggle={() => emitWorkspaceRepaintSolution({})}
                title={t('common.solution')}
                minSize={30}
                maxSize={70}
            >
                {charts && (
                    <WysiwygEditor
                        content={localSolutionRef.current}
                        contentGetter={() => localSolutionRef.current}
                        onChange={(solution) => {
                            handleSolutionChange(solution)
                            setCurrentSolution(solution)
                        }}
                        handleChartAdded={handleChartAdded}
                        handleChartRemoved={handleChartRemoved}
                        startingCharts={charts}
                        placeholder={t('common.solveHere')}
                        className='max-w-none flex-1'
                    />
                )}
            </WorkspacePane>
        </ResizablePanelGroup>
    )
}

